import { LoadingButton } from "@mui/lab";
import { Grid, IconButton, Modal, Slider, Stack, Typography, Alert, Snackbar } from "@mui/material";
import { Mp3Encoder } from "lamejs";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import RecordIcon from "@mui/icons-material/FiberManualRecord";
import PauseIcon from "@mui/icons-material/PauseRounded";
import PlayIcon from "@mui/icons-material/PlayArrowRounded";
import { default as StopIcon, default as StopRoundedIcon } from "@mui/icons-material/StopRounded";
import MicIcon from "@mui/icons-material/Mic";

import { Fileuploadrequestcall } from "../../request/Fileuploadrequest";
import { Postrequestcall } from "../../request/Postrequest";
import { SAVE_CLIENT_TAKE_5, SAVE_CLIENT_TAKE_5_RECORDING } from "../../constant/Apipath";
import { ClientEnglishFallback } from "../constant/ClientEnglishFallback";
import { BILightGrey } from "../../assets/helpers";
import VolumeReactiveVisualizer from "../../common/VolumeReactiveVisualizer";

export default function ClientTakeFiveAddModel({ isOpen, jobData, onClose, take5Id, reLoadData }) {
  const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);

  const audioRef = useRef(null);
  const recordingInterval = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [blobMp3, setBlobMp3] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [currentState, setCurrentState] = useState("Home");
  const [recordingTime, setRecordingTime] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [micPermission, setMicPermission] = useState(null);
  const [isRecording, setIsRecording] = useState(false);

  //for multi language
  const LANGUAGE = ClientEnglishFallback.ClientTakeFiveAddModel(
    multiLanguageData?.ClientTakeFiveAddModel || {}
  );

  const {
    TEXT_MICROPHONE_ACCESS_ERROR,
    TEXT_RECORDING_ERROR,
    TEXT_UNKOWN_ERROR,
    TEXT_ALLOW_MICROPHONE,
    TEXT_INVALID_DATA,
    TEXT_PROCESSING_ERROR,
    TEXT_ERROR_READING_AUDIO,
    TEXT_CONVERTING_ERROR,
    TEXT_NO_VALID_RECORDING,
    TEXT_FAILED_TO_SAVE,
    TEXT_FAILED_TO_START,
    TEXT_CHECK_SURROUNDING,
    TEXT_INPUT_TIMESHEET,
    TEXT_CHECK_WHETHER,
    TEXT_REPORT_DANGER,
    TEXT_MICROPHONE_PERMISSION,
    TEXT_RECORD_MESSAGE,
    TEXT_PAUSING_ERROR,
    TEXT_RESUMEING_ERROR,
    TEXT_ERROR_STOPPING,
    TEXT_ERROR_PLAYING,
    TEXT_SAVE_BTN
  } = LANGUAGE;

  // Check for microphone permission when component mounts
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        const result = await navigator.mediaDevices.getUserMedia({ audio: true });
        // If successful, clean up the stream immediately
        if (result) {
          const tracks = result.getTracks();
          tracks.forEach((track) => track.stop());
          setMicPermission(true);
        }
      } catch (err) {
        console.error("checkMicPermission-> Microphone permission denied: ", err);
        setMicPermission(false);
        setErrorMessage(TEXT_MICROPHONE_ACCESS_ERROR);
        setShowError(true);
      }
    };

    if (isOpen) {
      checkMicPermission();
    }

    return () => {
      // Clean up resources when component unmounts
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      clearInterval(recordingInterval.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isOpen]);

  // Request microphone permission
  const requestMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop()); // Clean up immediately
      setMicPermission(true);
      setShowError(false);
    } catch (err) {
      console.error("requestMicPermission-> Couldn't get microphone permission: ", err);
      setErrorMessage(TEXT_ALLOW_MICROPHONE);
      setShowError(true);
      setMicPermission(false);
    }
  };

  const convertToMp3 = async (audioBlob) => {
    if (!audioBlob || !(audioBlob instanceof Blob)) {
      setErrorMessage(TEXT_INVALID_DATA);
      setShowError(true);
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const arrayBuffer = reader.result;
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const samples = audioBuffer.getChannelData(0);
          const samplesInt16 = new Int16Array(samples?.length);

          for (let i = 0; i < samples?.length; i++) {
            samplesInt16[i] = samples[i] < 0 ? samples[i] * 0x8000 : samples[i] * 0x7fff;
          }

          const mp3Encoder = new Mp3Encoder(1, audioBuffer.sampleRate, 128);
          const mp3Data = [];

          const mp3Buf = mp3Encoder.encodeBuffer(samplesInt16);
          if (mp3Buf?.length > 0) {
            mp3Data.push(mp3Buf);
          }

          const flushBuf = mp3Encoder.flush();
          if (flushBuf?.length > 0) {
            mp3Data.push(flushBuf);
          }

          const mp3Blob = new Blob(mp3Data, { type: "audio/mp3" });
          setBlobMp3(mp3Blob);

          // Clean up previous audio URL if it exists
          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
          }

          const mp3Url = URL.createObjectURL(mp3Blob);
          setAudioUrl(mp3Url);

          // Close audio context to prevent memory leaks
          audioContext.close();
        } catch (err) {
          console.error("convertToMp3-> Error converting audio: ", err);
          setErrorMessage(TEXT_PROCESSING_ERROR);
          setShowError(true);
        }
      };

      reader.onerror = () => {
        setErrorMessage(TEXT_ERROR_READING_AUDIO);
        setShowError(true);
      };

      reader.readAsArrayBuffer(audioBlob);
    } catch (err) {
      console.error("convertToMp3-> Error in MP3 conversion: ", err);
      setErrorMessage(TEXT_CONVERTING_ERROR);
      setShowError(true);
    }
  };

  const onSavePressed = async () => {
    if (!blobMp3 || !(blobMp3 instanceof Blob)) {
      setErrorMessage(TEXT_NO_VALID_RECORDING);
      setShowError(true);
      return;
    }

    setSaveLoading(true);

    try {
      const obj = {
        UserId: loginData?.userInformation?.UserId,
        ReturnRecordId: true,
        ReturnRecordError: true,
        SaveList: [
          {
            Id: take5Id || 0,
            ClientEmployeeId: selectedClientData?.ClientEmployeeId,
            ClientEmployeeJobId: jobData?.id,
            ModifiedBy: loginData?.userInformation?.UserId
          }
        ]
      };

      const result = await Postrequestcall(SAVE_CLIENT_TAKE_5, obj, loginData?.accessToken);

      if (!result?.data?.data?.[0]?.objData?.id) {
        throw new Error("Failed to get valid ID from server");
      }

      const formData = new FormData();
      formData.append("Id", 0);
      formData.append("ClientTake5Id", Number(result?.data?.data?.[0]?.objData?.id));
      formData.append("FileName", blobMp3, "recording.mp3");
      formData.append("ModifiedBy", loginData?.userInformation?.UserId);

      const res = await Fileuploadrequestcall(
        SAVE_CLIENT_TAKE_5_RECORDING,
        formData,
        loginData?.accessToken
      );

      if (res.status === 201) {
        reLoadData();
        resetData();
      } else {
        throw new Error("Failed to upload recording");
      }
    } catch (error) {
      console.error("onSavePressed-> Error saving recording: ", error);
      setErrorMessage(TEXT_FAILED_TO_SAVE);
      setShowError(true);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;

    const time = e.target.value;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;

    setCurrentTime(audioRef.current.currentTime);

    if (audioRef.current.currentTime >= audioRef.current.duration) {
      console.log("Audio playback finished");
      setIsPlaying(false);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 0);
    }
  };

  const startRecordingTimer = () => {
    clearInterval(recordingInterval.current);
    recordingInterval.current = setInterval(() => {
      setRecordingTime((prevTime) => prevTime + 1);
    }, 1000);
  };

  const resetRecording = () => {
    clearInterval(recordingInterval.current);
    setCurrentState("Home");
    setIsPaused(false);
    setRecordingTime(0);
    setIsRecording(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const resetData = () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }

      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      clearInterval(recordingInterval.current);
      setCurrentState("Home");
      setIsPlaying(false);
      setRecordingTime(0);
      setIsPaused(false);
      setAudioUrl(null);
      setBlobMp3(null);
      setAudioBlob(null);
      setDuration(0);
      setCurrentTime(0);
      setIsRecording(false);

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }

      onClose();
    } catch (e) {
      console.error("Error in resetData:", e);
    }
  };

  const handleStartRecording = async () => {
    if (micPermission === false) {
      await requestMicPermission();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      const audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
        setAudioBlob(audioBlob);
        convertToMp3(audioBlob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      startRecordingTimer();
      setCurrentState("Recording");
    } catch (err) {
      console.error("handleStartRecording-> Failed to start recording: ", err);
      setErrorMessage(TEXT_FAILED_TO_START);
      setShowError(true);
    }
  };

  const handlePauseRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        clearInterval(recordingInterval.current);
      }
    } catch (err) {
      console.error("Error pausing:", err);
      setErrorMessage(TEXT_PAUSING_ERROR);
      setShowError(true);
    }
  };

  const handleResumeRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        startRecordingTimer();
      }
    } catch (err) {
      console.error("Error resuming:", err);
      setErrorMessage(TEXT_RESUMEING_ERROR);
      setShowError(true);
    }
  };

  const handleStopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        setCurrentState("Playback");
        setIsRecording(false);
        clearInterval(recordingInterval.current);
      }
    } catch (err) {
      console.error("Error stopping:", err);
      setErrorMessage(TEXT_ERROR_STOPPING);
      setShowError(true);
    }
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  return (
    <>
      <Modal open={isOpen} onClose={resetData}>
        <div className="client-modal-responsive">
          <div className="action-button-container">
            <IconButton
              onClick={resetData}
              sx={{
                color:
                  selectedClientData?.AccentColor !== undefined && selectedClientData?.AccentColor
                    ? selectedClientData?.AccentColor
                    : BILightGrey
              }}>
              <CloseIcon />
            </IconButton>
          </div>
          <Grid container padding={1} display="flex" flexDirection="column">
            <Stack display="flex" justifyContent="center" alignItems="center">
              <span>
                <Typography>{TEXT_CHECK_SURROUNDING}</Typography>
                <Typography>{TEXT_INPUT_TIMESHEET}</Typography>
                <Typography>{TEXT_CHECK_WHETHER}</Typography>
                <Typography>{TEXT_REPORT_DANGER}</Typography>
              </span>
            </Stack>
          </Grid>

          <Snackbar open={showError} autoHideDuration={6000} onClose={handleCloseError}>
            <Alert onClose={handleCloseError} severity="error" sx={{ width: "100%" }}>
              {errorMessage}
            </Alert>
          </Snackbar>

          <Grid container display="flex" flexDirection="column">
            {currentState === "Home" && (
              <Stack display="flex" justifyContent="center" alignItems="center" height="200px">
                <Typography style={{ color: "lightgrey" }}>
                  {micPermission === false ? TEXT_MICROPHONE_PERMISSION : TEXT_RECORD_MESSAGE}
                </Typography>
              </Stack>
            )}

            {currentState === "Recording" && (
              <>
                <Stack
                  position="relative"
                  height="200px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center">
                  {/* Use the real audio-reactive visualizer */}
                  {mediaRecorderRef.current && (
                    <VolumeReactiveVisualizer
                      mediaRecorder={mediaRecorderRef.current}
                      width="100%"
                      height={200}
                      barColor="grey"
                    />
                  )}

                  <span
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "red",
                      height: "150px",
                      width: "2px",
                      zIndex: 10
                    }}
                  />
                </Stack>

                <Stack
                  display="flex"
                  flexDirection="row"
                  justifyContent="center"
                  alignItems="center"
                  margin="10px 0"
                  gap={1}>
                  <span
                    className={!isPaused ? "pulse" : ""}
                    style={{
                      backgroundColor: "red",
                      height: "10px",
                      width: "10px",
                      borderRadius: "50%"
                    }}
                  />
                  <Typography>{moment.utc(recordingTime * 1000).format("m:ss")}</Typography>
                </Stack>
              </>
            )}

            {currentState === "Playback" && audioUrl && (
              <Grid container padding={1} size={12}>
                <Grid size={12}>
                  <Slider
                    fullWidth
                    value={currentTime || 0}
                    min={0}
                    step={0.01}
                    max={duration || 0}
                    onChange={handleSeek}
                    aria-labelledby="audio-slider"
                    sx={{ color: selectedClientData?.AccentColor }}
                  />
                  <Stack display="flex" flexDirection="row" justifyContent="space-between">
                    <Typography>{moment.utc(currentTime * 1000).format("m:ss")}</Typography>
                    <Typography>{moment.utc(duration * 1000).format("m:ss")}</Typography>
                  </Stack>
                </Grid>
                <audio
                  type="audio/mp3"
                  hidden
                  controls
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  name="media"
                  ref={audioRef}
                  src={audioUrl}
                />
              </Grid>
            )}
          </Grid>

          <Grid container display="flex" flexDirection="column">
            {currentState === "Home" && (
              <Stack
                marginTop="20px"
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={2}>
                {micPermission === false ? (
                  <IconButton
                    onClick={requestMicPermission}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      padding: "0px"
                    }}>
                    <MicIcon
                      style={{
                        height: "70px",
                        width: "70px",
                        color: "red"
                      }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={handleStartRecording}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      padding: "0px"
                    }}>
                    <RecordIcon
                      style={{
                        height: "70px",
                        width: "70px",
                        color: "red"
                      }}
                    />
                  </IconButton>
                )}
              </Stack>
            )}

            {currentState === "Recording" && (
              <Stack
                marginTop="20px"
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
                gap={2}>
                {!isPaused ? (
                  <IconButton
                    onClick={handlePauseRecording}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%"
                    }}>
                    <PauseIcon
                      style={{
                        fontSize: "50px",
                        color: "red"
                      }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={handleResumeRecording}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%"
                    }}>
                    <RecordIcon
                      style={{
                        fontSize: "50px",
                        color: "red"
                      }}
                    />
                  </IconButton>
                )}
                <IconButton
                  onClick={handleStopRecording}
                  style={{
                    backgroundColor: "#ebebeb",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "50%"
                  }}>
                  <StopIcon
                    style={{
                      fontSize: "50px",
                      color: "red"
                    }}
                  />
                </IconButton>
              </Stack>
            )}

            {currentState === "Playback" && (
              <>
                <Stack
                  marginTop="20px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}>
                  {isPlaying ? (
                    <IconButton
                      onClick={() => {
                        if (audioRef.current) {
                          audioRef.current.pause();
                          setIsPlaying(false);
                        }
                      }}
                      style={{
                        backgroundColor: "#ebebeb",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%"
                      }}>
                      <StopRoundedIcon
                        style={{
                          height: "50px",
                          width: "50px",
                          color: "red"
                        }}
                      />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={() => {
                        if (audioRef.current) {
                          audioRef.current.currentTime = 0;
                          audioRef.current.play().catch((err) => {
                            console.error("Error playing audio:", err);
                            setErrorMessage(TEXT_ERROR_PLAYING);
                            setShowError(true);
                          });
                          setIsPlaying(true);
                        }
                      }}
                      style={{
                        backgroundColor: "#ebebeb",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%"
                      }}>
                      <PlayIcon
                        style={{
                          fontSize: "50px"
                        }}
                      />
                    </IconButton>
                  )}

                  <IconButton
                    onClick={resetRecording}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      width: "70px",
                      height: "70px"
                    }}>
                    <DeleteIcon
                      style={{
                        fontSize: "50px"
                      }}
                    />
                  </IconButton>
                </Stack>
                <LoadingButton
                  onClick={onSavePressed}
                  loading={saveLoading}
                  loadingPosition="start"
                  startIcon={<></>}
                  fullWidth
                  style={{
                    textTransform: "none",
                    backgroundColor: selectedClientData?.AccentColor || "#1976d2",
                    color: "white",
                    marginTop: "20px",
                    borderRadius: "10px"
                  }}>
                  {TEXT_SAVE_BTN}
                </LoadingButton>
              </>
            )}
          </Grid>
        </div>
      </Modal>
    </>
  );
}
